import { Button, Flex, Text, Card, Table, Box, Badge } from "@radix-ui/themes";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { SubscriptionPlan, formatCurrency } from "@/types/breeze/AnythinkPay";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorCard from "@/components/ui/ErrorCard";

interface SubscriptionPlansListProps {
  showHeader?: boolean;
}

export default function SubscriptionPlansList({
  showHeader = true,
}: SubscriptionPlansListProps) {
  const { currentTenant, safeNavigate } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const {
    data: plans,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: () => anythinkPayService.getSubscriptionPlans(),
  });

  const deletePlan = useMutation({
    mutationFn: (id: number) => anythinkPayService.deleteSubscriptionPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      toast.error(
        (typeof data === "string" && data) ||
          data?.message ||
          "Failed to delete plan.",
      );
    },
  });

  const handleDelete = async (plan: SubscriptionPlan) => {
    const confirmed = await confirm({
      title: "Delete plan",
      message: `Are you sure you want to delete "${plan.plan_name}"? A plan can only be deleted once nobody is subscribed to it; to stop new sign-ups while customers are still subscribed, set it inactive instead. This cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });

    if (confirmed) {
      deletePlan.mutate(plan.id);
    }
  };

  if (isLoading) return <LoadingScreen fullPage={false} />;
  if (error) return <ErrorCard error={error.message} />;

  return (
    <Flex direction="column" gap="4">
      {showHeader && (
        <Flex justify="between" align="center">
          <Text size="2" color="gray">
            {plans?.length ?? 0} plan(s)
          </Text>
          <Button
            onClick={() =>
              safeNavigate(
                `/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/plans/create`,
              )
            }
          >
            <Plus size={16} />
            New plan
          </Button>
        </Flex>
      )}

      {!plans?.length ? (
        <Card>
          <Box p="6">
            <Text size="3" color="gray" as="p" mb="4">
              No subscription plans yet. Create one to define what customers
              can subscribe to.
            </Text>
            <Button
              onClick={() =>
                safeNavigate(
                  `/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/plans/create`,
                )
              }
            >
              <Plus size={16} />
              Create your first plan
            </Button>
          </Box>
        </Card>
      ) : (
        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Plan name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Product name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Amount</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Billing</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {plans?.map((p) => (
                <Table.Row key={p.id}>
                  <Table.Cell>
                    <Text weight="medium">{p.plan_name}</Text>
                  </Table.Cell>
                  <Table.Cell>{p.name}</Table.Cell>
                  <Table.Cell>
                    {formatCurrency(p.amount, p.currency)} /{" "}
                    {p.interval_count}
                    {p.billing_interval}
                  </Table.Cell>
                  <Table.Cell>
                    {p.interval_count} {p.billing_interval}(s)
                  </Table.Cell>
                  <Table.Cell>
                    {p.is_active ? (
                      <Badge color="green">Active</Badge>
                    ) : (
                      <Badge color="gray">Retired</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap="2">
                      <Button
                        variant="soft"
                        size="1"
                        onClick={() =>
                          navigate(
                            `/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/plans/${p.id}`,
                          )
                        }
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="soft"
                        color="red"
                        size="1"
                        onClick={() => handleDelete(p)}
                        disabled={deletePlan.isPending}
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
      )}
    </Flex>
  );
}
